import { storedAnonId } from '../lib/storage';
import { api } from '../lib/api';
import { normalizeTotals } from '../lib/reactions';
import type {
  Comment,
  CommentsResponse,
  CreateReactionBody,
  Reaction,
  ReactionsResponse,
  SharePayload, VideoSettings } from '../lib/types';

/**
 * The share surface is deliberately auth-optional: the share id *is* the
 * credential. A signed-in viewer sends their token so private team videos
 * resolve, and so their view is attributed to their account instead of an
 * anonymous browser id.
 */

/**
 * The token a viewer got for giving an email, per recording, in this
 * browser. Losing it only means being asked again.
 */
export function storedViewerToken(shareId: string): string | undefined {
  try {
    return localStorage.getItem(`slate.viewer.${shareId}`) ?? undefined;
  } catch {
    return undefined;
  }
}

export function rememberViewerToken(shareId: string, token: string): void {
  try {
    localStorage.setItem(`slate.viewer.${shareId}`, token);
  } catch {
    // Private mode; the session still works, they will be asked next time.
  }
}

export function fetchShare(shareId: string): Promise<SharePayload> {
  return api<SharePayload>(`/v1/share/${shareId}`, { auth: 'optional', viewerToken: storedViewerToken(shareId) });
}

/** POST /v1/share/:shareId/viewer: an email, for a token. */
export async function giveViewerEmail(shareId: string, email: string): Promise<string> {
  const { token } = await api<{ token: string }>(`/v1/share/${shareId}/viewer`, {
    method: 'POST',
    auth: 'optional',
    body: { email },
  });
  rememberViewerToken(shareId, token);
  return token;
}

export function updateVideoSettings(videoId: string, patch: Partial<VideoSettings>): Promise<VideoSettings> {
  return api<VideoSettings>(`/v1/videos/${videoId}/settings`, { method: 'PATCH', body: patch });
}

export function fetchComments(shareId: string): Promise<Comment[]> {
  return api<CommentsResponse>(`/v1/share/${shareId}/comments`, { auth: 'optional' }).then(
    (response) => response.comments,
  );
}

export function postComment(
  shareId: string,
  body: { bodyText: string; authorName?: string; timecodeSec?: number },
): Promise<Comment> {
  return api<Comment>(`/v1/share/${shareId}/comments`, { method: 'POST', body, auth: 'optional' });
}

/**
 * Reactions use the same auth-optional rule as comments: the share id is the
 * credential, and a 404 means "not yours to see" as much as it means "gone".
 */
export function fetchReactions(shareId: string): Promise<ReactionsResponse> {
  return api<ReactionsResponse>(`/v1/share/${shareId}/reactions`, { auth: 'optional' }).then(
    (response) => ({
      reactions: Array.isArray(response?.reactions) ? response.reactions : [],
      totals: normalizeTotals(response?.totals),
    }),
  );
}

export function postReaction(shareId: string, body: CreateReactionBody): Promise<Reaction> {
  return api<Reaction>(`/v1/share/${shareId}/reactions`, {
    method: 'POST',
    body,
    auth: 'optional',
  });
}

export function postView(
  shareId: string,
  body: { anonId?: string; progressSec?: number; completed?: boolean },
): Promise<void> {
  return api<void>(`/v1/share/${shareId}/views`, { method: 'POST', body, auth: 'optional' });
}

/**
 * The viewer id for a browser with no account.
 *
 * The API mints it and signs it; a browser cannot make one up, so a thousand
 * beacons from one tab are one viewer and the reaction cap means something.
 * Kept in storage once minted. One request in flight at a time, so a page
 * that asks twice on load mints once.
 */
let minting: Promise<string> | null = null;

export function ensureAnonId(): Promise<string> {
  const have = storedAnonId.get();
  if (have && have.startsWith('anon_')) return Promise.resolve(have);
  if (!minting) {
    minting = api<{ anonId: string }>('/v1/share/anon', { method: 'POST', auth: 'none' })
      .then((response) => {
        storedAnonId.set(response.anonId);
        return response.anonId;
      })
      .finally(() => {
        minting = null;
      });
  }
  return minting;
}

/** The API refused the id we hold (a rotated secret, say): forget it and mint again next time. */
export function forgetAnonId(): void {
  storedAnonId.clear();
}
